/**
 * /start – Child device pairing entry point.
 * Guards: parents are redirected away. PairCodeDisplay only mounts after guards pass.
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { generateShortCode, sha256Hex } from "@/lib/crypto";
import { getDeviceIdHash, hasChildSession, storeChildSession } from "@/lib/device";
import { useAuth } from "@/hooks/useAuth";
import { useFamily } from "@/hooks/useFamily";
import { Button } from "@/components/ui/button";

export default function StartEntry() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { hasFamily, isLoading } = useFamily();
  const didRedirect = useRef(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (didRedirect.current) return;
    if (loading || isLoading) return;

    if (user && hasFamily) {
      didRedirect.current = true;
      navigate("/dashboard", { replace: true });
      return;
    }
    if (user && !hasFamily) {
      didRedirect.current = true;
      navigate("/onboarding", { replace: true });
      return;
    }
    if (hasChildSession()) {
      didRedirect.current = true;
      navigate("/child/access", { replace: true });
      return;
    }

    setReady(true);
  }, [loading, isLoading, user, hasFamily, navigate]);

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Laster…</p>
      </div>
    );
  }

  return <PairCodeDisplay />;
}

function PairCodeDisplay() {
  const navigate = useNavigate();
  const [code, setCode] = useState<string | null>(null);
  const [codeHash, setCodeHash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval>>();
  const expiresRef = useRef<number>(0);

  const registerCode = useCallback(async () => {
    setError(null);
    setExpired(false);
    try {
      const newCode = generateShortCode(6);
      const [hash, deviceHash] = await Promise.all([
        sha256Hex(newCode),
        getDeviceIdHash(),
      ]);

      const { error: rpcErr } = await supabase.rpc("register_device_pair_code" as any, {
        _code_hash: hash,
        _code_plain: newCode,
        _device_id_hash: deviceHash,
      });

      if (rpcErr) {
        setError("Kunne ikke opprette kode. Prøv igjen.");
        return;
      }

      setCode(newCode);
      setCodeHash(hash);
      expiresRef.current = Date.now() + 10 * 60 * 1000;
    } catch {
      setError("Kunne ikke opprette kode. Prøv igjen.");
    }
  }, []);

  useEffect(() => {
    registerCode();
  }, [registerCode]);

  useEffect(() => {
    if (!codeHash) return;

    const poll = async () => {
      if (Date.now() > expiresRef.current) {
        setExpired(true);
        if (pollRef.current) clearInterval(pollRef.current);
        return;
      }

      try {
        const deviceHash = await getDeviceIdHash();
        const { data, error: rpcErr } = await supabase.rpc("poll_device_pair_status" as any, {
          _code_hash: codeHash,
          _device_id_hash: deviceHash,
        });

        const rows = data as Array<{ child_id: string; session_token_plain: string }> | null;
        if (!rpcErr && rows && rows.length > 0) {
          const row = rows[0];
          storeChildSession(row.session_token_plain, row.child_id);
          if (pollRef.current) clearInterval(pollRef.current);
          navigate(`/child/${row.child_id}`, { replace: true });
        }
      } catch {
        // Ignore poll errors
      }
    };

    pollRef.current = setInterval(poll, 2000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [codeHash, navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 gap-6">
        <div className="text-5xl">⚠️</div>
        <p className="text-destructive text-center">{error}</p>
        <Button onClick={registerCode} className="rounded-xl">
          Prøv igjen
        </Button>
      </div>
    );
  }

  if (!code) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Oppretter kode…</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 gap-6">
      <div className="text-5xl">📱</div>
      <h1 className="text-2xl font-bold text-center">Koble denne enheten</h1>
      <p className="text-muted-foreground text-center max-w-xs">
        Vis denne koden til en voksen. De skriver den inn i Flyt-appen.
      </p>

      <div className="bg-muted rounded-2xl px-8 py-6">
        <p className="text-4xl font-mono font-bold tracking-[0.3em] text-center select-all">
          {code}
        </p>
      </div>

      {expired ? (
        <div className="text-center space-y-3">
          <p className="text-sm text-destructive">Koden har utløpt</p>
          <Button onClick={registerCode} variant="outline" className="rounded-xl">
            Lag ny kode
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground text-center animate-pulse">
          Venter på at en voksen skriver inn koden…
        </p>
      )}

      <Link
        to="/login"
        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        Er du forelder? Logg inn
      </Link>
    </div>
  );
}
